import { useCallback, useMemo } from "react";
import { useList, useOne } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type { BrandKit, ContactResponse } from "pages/media/types";
import type { AddonSettings } from "../context/PlaybookDraftContext";
import {
  seedAddonSettings,
  seedGeneralSettings,
  type MediaSeedDefaults,
} from "../data/addonApi";
import {
  emotionCapableContacts,
  voiceProfileId as contactVoiceId,
} from "../helpers/contactVoices";
import type { AddonId } from "../data/addons";

/**
 * Resolves the org's default media entities (brand kit / avatar / voices) the
 * seeds below inject. Shared by both hooks; the underlying queries are deduped
 * by React Query.
 */
const useMediaSeedDefaults = (): MediaSeedDefaults => {
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;

  const { data: preferencesData } = useOne({
    resource: "media/preferences",
    id: organizationId,
    queryOptions: {
      enabled: !!organizationId,
      refetchOnMount: false,
    },
  });

  const { data: contactsData } = useList<ContactResponse>({
    resource: `media/${organizationId}/contacts`,
    queryOptions: { enabled: !!organizationId },
  });

  // Only to validate the preference still points at an existing kit — a
  // deleted default must not ride into payloads as a dangling id.
  const { data: brandKitData } = useList<BrandKit>({
    resource: `media/${organizationId}/brand_kits?include_generated=false`,
    queryOptions: { enabled: !!organizationId },
  });

  return useMemo<MediaSeedDefaults>(() => {
    const preferences = preferencesData?.data as
      | { brand_kit_id?: string | null; contact_id?: string | null }
      | undefined;
    const contacts = contactsData?.data ?? [];
    const brandKits = brandKitData?.data ?? [];
    // Existence-checked (the old card's `withDefault` used to do this): a
    // preference pointing at a deleted entity reads as "no preference".
    const brandKitId = brandKits.some(
      (kit) => kit.id === preferences?.brand_kit_id
    )
      ? (preferences?.brand_kit_id ?? undefined)
      : undefined;
    const preferredContactId = contacts.some(
      (contact) => contact.id === preferences?.contact_id
    )
      ? (preferences?.contact_id ?? undefined)
      : undefined;
    // Org default avatar (falls back to the first contact), then its voice.
    const contactId = preferredContactId ?? contacts[0]?.id ?? undefined;
    const defaultContact =
      contacts.find((contact) => contact.id === contactId) ?? contacts[0];
    const voiceProfileId = defaultContact && contactVoiceId(defaultContact);
    // Podcast needs one voice per speaker role (Host, then Guest) and
    // both must support emotions, so resolve a distinct ordered pool of those
    // rather than starting from the org default avatar's voice.
    const emotionCapableVoiceProfileIds = emotionCapableContacts(contacts)
      .map(contactVoiceId)
      .filter(
        (id, index, all): id is string => !!id && all.indexOf(id) === index
      );
    return {
      brandKitId,
      contactId,
      voiceProfileId,
      emotionCapableVoiceProfileIds,
    };
  }, [preferencesData?.data, contactsData?.data, brandKitData?.data]);
};

/** Seeds an add-on's initial draft settings: card defaults + org default
 *  voices. Resolved centrally so an untouched add-on still carries them. */
export const useAddonSeeder = (): ((id: AddonId) => AddonSettings) => {
  const media = useMediaSeedDefaults();
  return useCallback((id: AddonId) => seedAddonSettings(id, media), [media]);
};

/** Background General seed (org default brand kit / avatar), merged under the
 *  user's general-card values at create time. */
export const useGeneralSeed = (): AddonSettings => {
  const media = useMediaSeedDefaults();
  return useMemo(() => seedGeneralSettings(media), [media]);
};
